/**
 * Campaign Prizes Tab Component
 * Shows campaign prizes in a table format
 */

import { type FC, memo, useMemo } from 'react';
import styles from './PrizesTab.module.css';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from '../../../../../../../shared/components/LoadingIndicator';
import { useCampaignPrizesQueryById } from '../../../../../hooks/data/useCampaignsQuery';
import CampaignTable from '../../../../shared/CampaignTable/CampaignTable';
import tableStyles from '../../../../shared/CampaignTable/CampaignTable.module.css';
import { formatNumberWithSpaces } from 'shared/utils/helpers';
import { useCampaignsStore } from 'features/campaigns/_exports';

const PrizesTab: FC = () => {
  const { t } = useTranslation();

  const selectedCampaign = useCampaignsStore((state) => state.getSelectedCampaign());
  const prizesQuery = useCampaignPrizesQueryById(selectedCampaign, true);

  const prizes = prizesQuery.data?.prizes || [];

  const columns = useMemo(() => [
    {
      name: t('campaigns.prizes', 'Prizes'),
      key: 'campaigns.prizes',
      cellClassName: styles.prizeValue
    },
    {
      name: t('campaigns.remaining', 'Remaining'),
      key: 'campaigns.remaining',
      className: styles.remainingCell
    },
    {
      name: t('campaigns.total', 'Total'),
      key: 'campaigns.total',
      className: styles.remainingCell
    }
  ], [t]);

  // Loading state
  if (prizesQuery.isLoading) {
    return (
      <div className={tableStyles.tabContainer}>
        <div className={styles.loaderContainer}>
          <LoadingIndicator show absolute />
        </div>
      </div>
    );
  }

  // Empty prizes state
  if (prizes.length === 0) {
    return (
      <div className={tableStyles.tabContainer}>
        <div className={styles.noDataWrapper}>
          <p className={styles.noDataText}>{t('campaigns.no_prizes', 'No prizes available')}.</p>
        </div>
      </div>
    );
  }

  return (
      <CampaignTable columns={columns}>
        {prizes.map((prize, index) => {
          return (
            <tr key={index} className={`${tableStyles.tableRow} ${styles.prizesTableRow}`}>
              <td className={tableStyles.tableCell}>
                <span className={styles.prizeAmountText}>
                  {formatNumberWithSpaces(prize.value)} <span>{prize.currencyCode}</span>
                </span>
              </td>
              <td className={`${tableStyles.tableCell} ${styles.remainingCell}`}>
                <span className={styles.remainingText}>{formatNumberWithSpaces(prize.remaining)}</span>
              </td>
              <td className={`${tableStyles.tableCell} ${styles.totalCell}`}>
                <span className={styles.remainingText}>{formatNumberWithSpaces(prize.count)}</span>
              </td>
            </tr>
          );
        })}
      </CampaignTable>

  );
};

export default memo(PrizesTab);
